import './payment-method-add-sheet.scss';

import Script from 'next/script';
import { ReactElement, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Divider } from '@sola/ark-core/atoms/divider/divider.js';
import { AddressSelectSupporter } from '@sola/ark-core/molecules/address-select/address-select-supporter.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.js';
import {
  FormSubmit,
  FormSubmitProps,
} from '@sola/ark-core/molecules/form/form.js';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { usePaymentMethodAdd } from '@sola/my-sdk/payment-methods/hooks.js';
import { AddressSelectOptions } from '@sola/schema/dotcom/shared/address.js';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.js';
import {
  PaymentMethodTypesEnum,
  SupporterAchDetails,
  SupporterCCDetails,
  SupporterCCFieldsEnum,
  SupporterPaymentMethodDetails,
} from '@sola/schema/dotcom/supporters/paymentMethods.js';
import {
  GraphQLCommitment,
  GraphQLSupporterPaymentMethod,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { getLongExpiryDate } from '@sola/shared-utils/credit-cards.js';
import { userErrorPaymentMethodCreate } from '@sola/shared-utils/errors/supporters.js';
import {
  isPaymentMethodAch,
  isPaymentMethodCard,
} from '@sola/shared-utils/my/payment-method-type-guards.js';
import { useSupporterBillingAddresses } from '@sola/supporter-sdk/hooks/payment-methods.js';
import { getNonRecurringCommitments } from '@sola/supporter-sdk/utils/commitments.js';

import { LocalAlert } from '../../molecules/local-alerts/local-alerts';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts';
import { PaymentACHFields } from '../../molecules/payment-method-add-form/payment-ach-fields';
import { PaymentCCFields } from '../../molecules/payment-method-add-form/payment-cc-fields';
import { PaymentMethodAddForm } from '../../molecules/payment-method-add-form/payment-method-add-form';
import { PaymentMethodSwitch } from '../../molecules/payment-method-switch/payment-method-switch';
import { SectionContent } from '../../molecules/section-content/section-content';
import {
  createPaymentMethodPartialSuccessAlert,
  createPaymentMethodSuccessAlert,
  createSupportAlert,
} from '../../utils/alerts';
import { getErrorTranslationKeys } from '../../utils/errors';
import {
  RecurringGivingOptions,
  RecurringGivingRadioOptionsEnum,
} from '../recurring-giving-options/recurring-giving-options';

type PaymentMethodAddSheetFooterProps = Pick<FormSubmitProps, 'isBusy'>;

const AddPaymentMethodSheetFooter: React.FC<
  PaymentMethodAddSheetFooterProps
> = ({ isBusy }) => {
  const { t } = useTranslation('core');

  return (
    <SheetFooter>
      <SheetActions>
        <SheetClose asChild>
          <ButtonStyle
            size="lg"
            purpose="neutral"
            variant="secondary"
            fullWidth
          >
            <button type="button">{t('core:actions.cancel')}</button>
          </ButtonStyle>
        </SheetClose>

        <FormSubmit
          iconBefore={<ArkIcon iconName="lock-01" />}
          isBusy={isBusy}
          disableUntilFilled
        >
          {t('core:actions.add')}
        </FormSubmit>
      </SheetActions>
    </SheetFooter>
  );
};

export interface PaymentMethodAddSheetProps {
  commitments?: GraphQLCommitment[];
  /**
   * This should only take in elements that resolve to html buttons.
   */
  openTrigger: ReactElement;
  onComplete?: (newPaymentMethod: GraphQLSupporterPaymentMethod) => void;
}
export const PaymentMethodAddSheet: React.FC<PaymentMethodAddSheetProps> = ({
  openTrigger,
  commitments = [],
  onComplete,
}) => {
  const { t } = useTranslation('my');
  const title = t('addPaymentMethod.form.title');
  const [isOpen, setIsOpen] = useState(false);
  const { addAlerts, clearAlerts } = useLocalAlerts();
  const {
    alerts: sheetAlerts,
    setAlerts: setSheetAlerts,
    clearAlerts: clearSheetAlerts,
    removeAlert: removeSheetAlert,
    handleOpenChange,
  } = useModalAlerts();
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodTypesEnum>(
    PaymentMethodTypesEnum.Ach,
  );
  const nonRecurringCommitments = getNonRecurringCommitments(commitments);

  const addPaymentMethod = usePaymentMethodAdd();
  const { supporterAddresses } = useSupporterBillingAddresses();

  const handleSubmit = async (
    paymentMethodDetails: SupporterPaymentMethodDetails,
    data: FieldValues,
  ) => {
    clearSheetAlerts();
    clearAlerts();

    try {
      let commitmentIds = [] as string[];

      if (data.recurringGiving === RecurringGivingRadioOptionsEnum.Yes) {
        commitmentIds =
          nonRecurringCommitments
            ?.map((commitment) => commitment.internalId)
            .filter((id): id is string => typeof id === 'string') ?? [];
      }

      const { paymentMethod: addedPaymentMethod, isPartialSuccess } =
        await addPaymentMethod.mutateAsync({
          paymentMethod: paymentMethodDetails,
          commitmentIds,
        });

      if (!addedPaymentMethod) {
        throw userErrorPaymentMethodCreate();
      }

      const alerts = [
        createPaymentMethodSuccessAlert(
          addedPaymentMethod,
          'addPaymentMethod.success.action',
        ),
      ];

      if (isPartialSuccess) {
        alerts.push(createPaymentMethodPartialSuccessAlert());
      }

      addAlerts(alerts);

      if (onComplete) {
        onComplete(addedPaymentMethod);
      }

      handleOpenChange(false, setIsOpen);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(
        SupporterErrorTypeEnum.PaymentMethodCreate,
        errors,
      );
      setSheetAlerts(
        translationKeys.map((translationKey) => {
          return createSupportAlert(translationKey);
        }),
      );
    }
  };

  const handleAchSubmit = async (data: FieldValues) => {
    const achDetails = data as SupporterAchDetails;
    await handleSubmit(
      { ...achDetails, type: PaymentMethodTypesEnum.Ach },
      data,
    );
  };

  const handleCardSubmit = async (data: FieldValues) => {
    const { addressOption, ...rest } = data as SupporterCCDetails;
    let cardDetails = rest;
    const expirationDate = getLongExpiryDate(
      data[SupporterCCFieldsEnum.expirationDate],
    );

    if (addressOption !== AddressSelectOptions.Add) {
      const supporterAddress = supporterAddresses?.get(addressOption);
      cardDetails = {
        ...cardDetails,
        ...supporterAddress?.billingAddress,
      };
    }

    await handleSubmit(
      {
        ...cardDetails,
        addressOption,
        expirationDate,
        type: PaymentMethodTypesEnum.Card,
      },
      data,
    );
  };

  return (
    <>
      <Script src="/tokenex.js" strategy="lazyOnload" />
      <Sheet
        open={isOpen}
        onOpenChange={(open) => handleOpenChange(open, setIsOpen)}
      >
        <SheetTrigger>{openTrigger}</SheetTrigger>
        <SheetContent title={title}>
          <SheetHeader>{title}</SheetHeader>
          <SheetBody>
            <p
              id="selectPaymentMethodType"
              className="body-sm md:body-md body-neutral-mild"
            >
              {t('addPaymentMethod.form.leadin')}
            </p>

            {sheetAlerts.length > 0 && (
              <div className="mt-comfy-sm md:mt-comfy-lg">
                {sheetAlerts.map((sheetAlert) => (
                  <LocalAlert
                    key={sheetAlert.id}
                    alert={sheetAlert}
                    className="my-comfy-xs"
                    onClose={removeSheetAlert}
                  />
                ))}
              </div>
            )}

            <form aria-describedby="selectPaymentMethodType">
              <PaymentMethodSwitch
                setSelectedPaymentMethod={setSelectedMethod}
              />
            </form>

            <div className={isPaymentMethodAch(selectedMethod) ? '' : 'hidden'}>
              <Alert
                variant="positive"
                title={t('addPaymentMethod.alert.title')}
                message={t('addPaymentMethod.alert.message')}
              />

              <Divider purpose="neutral" className="my-comfy-xl" />

              <PaymentMethodAddForm onSubmit={handleAchSubmit}>
                <SectionContent
                  heading={t('payments.methodForm.formTitle.bank')}
                >
                  <Fieldset id="ach-fields">
                    <PaymentACHFields />
                  </Fieldset>
                </SectionContent>

                <Divider purpose="neutral" className="my-comfy-xl" />

                {/* Recurring giving options */}
                {!!nonRecurringCommitments?.length && (
                  <SectionContent
                    heading={t('payments.methodForm.formTitle.recurringGiving')}
                  >
                    <RecurringGivingOptions
                      commitments={nonRecurringCommitments}
                    />
                  </SectionContent>
                )}

                <AddPaymentMethodSheetFooter
                  isBusy={addPaymentMethod.isPending}
                />
              </PaymentMethodAddForm>
            </div>

            <div
              className={isPaymentMethodCard(selectedMethod) ? '' : 'hidden'}
            >
              <Divider purpose="neutral" className="my-comfy-xl" />
              <PaymentMethodAddForm onSubmit={handleCardSubmit}>
                <div>
                  <SectionContent
                    heading={t('payments.methodForm.formTitle.card')}
                  >
                    <Fieldset id="cc-fields">
                      <PaymentCCFields />
                    </Fieldset>
                  </SectionContent>

                  <Divider purpose="neutral" className="my-comfy-xl" />

                  <SectionContent
                    heading={t('payments.methodForm.formTitle.billingInfo')}
                  >
                    <AddressSelectSupporter
                      label={t('payments.methodForm.fieldLabels.billingInfo')}
                    />
                  </SectionContent>
                </div>

                <Divider purpose="neutral" className="my-comfy-xl" />

                {/* Recurring giving options */}
                {!!nonRecurringCommitments?.length && (
                  <SectionContent
                    heading={t('payments.methodForm.formTitle.recurringGiving')}
                  >
                    <RecurringGivingOptions
                      commitments={nonRecurringCommitments}
                    />
                  </SectionContent>
                )}

                <AddPaymentMethodSheetFooter
                  isBusy={addPaymentMethod.isPending}
                />
              </PaymentMethodAddForm>
            </div>
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
};
